import { useTranslation } from 'react-i18next';
import { useState, type FormEvent } from 'react';
import { uploadShipmentDocument, type UploadDocumentType } from '../../api/endpoints/admin';
import { useMutationHook } from '../../hooks/useMutationHook';
import styles from '../../components/domain/FormModal.module.css';
import { DialogShell } from '../../components/ui/DialogShell';
import { useDialogDismiss, useIsDirty } from '../../components/ui/dialogDismiss';
import { DialogTitleRow } from '../../help/DialogTitleRow';

const DOC_TYPES: { value: UploadDocumentType; label: string }[] = [
  { value: 'VEHICLE_RC', label: 'Vehicle RC' },
  { value: 'VEHICLE_PHOTO', label: 'Vehicle Picture' },
  { value: 'LR_EWAY', label: 'LR / e-way bill' },
  { value: 'LOADING_PHOTO', label: 'Loading photo' },
  { value: 'ID_PROOF', label: 'ID proof' },
  { value: 'POD', label: 'Proof of delivery (POD)' },
  { value: 'OTHER', label: 'Other' },
];

interface Props {
  shipmentId: number;
  onClose: () => void;
}

/** Ops document upload (e.g. paper LR scanned at the office). */
export function DocumentUploadModal({ shipmentId, onClose }: Props) {
  const { t } = useTranslation();
  const [type, setType] = useState<UploadDocumentType>('LR_EWAY');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const uploadMut = useMutationHook(() => uploadShipmentDocument(shipmentId, type, file!), {
    successMessage: 'Document uploaded.',
    invalidateKeys: [
      ['admin', 'shipments', 'documents', shipmentId],
      ['admin', 'documents'],
      ['admin', 'dashboard'],
    ],
    onSuccess: onClose,
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!file) return setError('Please choose a file to upload.');
    if (file.size > 10 * 1024 * 1024) return setError('File must be smaller than 10 MB.');
    setError(null);
    uploadMut.mutate(undefined);
  };

  const dirty = useIsDirty({ type, file });
  const dismiss = useDialogDismiss({ dirty, busy: uploadMut.isLoading, onClose });

  return (
    <DialogShell
      dismiss={dismiss}
      label={t('shipments.documentUploadModal.title')}
      scrimClassName={styles.scrim}
      panelClassName={styles.panel}
    >
      <DialogTitleRow context="shipment-documents" title={t('shipments.documentUploadModal.title')}>
        <h2 className={styles.title}>{t('shipments.documentUploadModal.title')}</h2>
      </DialogTitleRow>

      <form onSubmit={submit}>
        <label className={styles.label}>{t('shipments.documentUploadModal.typeLabel')}</label>
        <select
          className={styles.select}
          value={type}
          onChange={(e) => setType(e.target.value as UploadDocumentType)}
        >
          {DOC_TYPES.map((d) => (
            <option key={d.value} value={d.value}>
              {d.label}
            </option>
          ))}
        </select>

        <label className={styles.label}>{t('shipments.documentUploadModal.fileLabel')}</label>
        <input
          className={styles.input}
          type="file"
          accept="image/*,application/pdf"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.actions}>
          <button type="button" className={styles.cancel} onClick={dismiss.requestClose}>
            {t('common.actions.cancel')}
          </button>
          <button type="submit" className={styles.submit} disabled={uploadMut.isLoading}>
            {uploadMut.isLoading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </form>
    </DialogShell>
  );
}
